import React, { useMemo } from "react";
import { getLocale, useIntl } from "umi";
import { IsSingleDay, jumpDetail } from "@/pages/dashboard/utils/utils";
import SvgIcon from "@/pages/home/components/SvgIcon";
import ACard from "../ACard";

interface Props {
  gameCode: string;
  filterVal: any;
  accumated: string;
  config: any;
}

export const useCommonRankShowData = (props: Props) => {
  const { accumated, filterVal, gameCode, config } = props;
  const { componentMetrics, metric, jumpUrl } = config || {};
  const { rangeTime = [], timeType, } = filterVal || {};
  const { formatMessage } = useIntl();
  const locale = getLocale();
  const isEn = locale === 'en-US';
  const { titleName, tableColumnTitle, prefix, rangeTableColumnTitle }: { titleName: string; tableColumnTitle: string; prefix: string; rangeTableColumnTitle: string; } = useMemo(() => {
    const titleNameObj: any = {
      day: componentMetrics?.[isEn ? 'enTitleName' : 'cnTitleName'],
      week: componentMetrics?.[isEn ? 'enWeekTitleName' : 'cnWeekTitleName'],
      month: componentMetrics?.[isEn ? 'enMonthTitleName' : 'cnMonthTitleName'],
    };

    const titleResultObj: any = {
      day: componentMetrics?.[isEn ? 'enName' : 'cnName'],
      week: componentMetrics?.[isEn ? 'enWeekName' : 'cnWeekName'],
      month: componentMetrics?.[isEn ? 'enMonthName' : 'cnMonthName'],
    };
    const rangeTitleObj: any = {
      day: componentMetrics?.[isEn ? 'rangeEnName' : 'rangeCnName'],
      week: componentMetrics?.[isEn ? 'rangeEnWeekName' : 'rangeCnWeekName'],
      month: componentMetrics?.[isEn ? 'rangeEnMonthName' : 'rangeCnMonthName'],
    };

    return {
      titleName: titleNameObj?.[timeType?.toLocaleLowerCase()] || titleNameObj?.day,
      tableColumnTitle: titleResultObj?.[timeType?.toLocaleLowerCase()] || titleResultObj?.day,
      prefix: componentMetrics?.prefix,
      rangeTableColumnTitle: rangeTitleObj?.[timeType?.toLocaleLowerCase()],
    };
  }, [locale, timeType, componentMetrics]);

  const jumpToLink = () => {
    if (jumpUrl) {
      const formValue = {
        filterValue: filterVal,
        timeType,
        diff: filterVal?.diff
      };
      jumpDetail(`/dashboard/game/${gameCode}${jumpUrl}`, formValue, metric);
    }
  };

  const titleContent = useMemo(() => {
    const titleContentObj: any = {
      country: formatMessage({ id: 'common_country_top_10' }),
      region: formatMessage({ id: 'common_region_top_10' }),
      channel: formatMessage({ id: 'common_channel_top_10' }),
      zone: formatMessage({ id: 'common_zone_top_10' }),
      os: formatMessage({ id: 'common_platfrom_top_10' }),
      lang: formatMessage({ id: 'common_language_top_10' }),
    };

    const noTopTitle = accumated === 'os' || accumated === 'lang';
    const isTrim = noTopTitle && !isEn;
    const accumatedTitle = titleContentObj?.[accumated];
    const name = isTrim ? `${accumatedTitle}${titleName}` : `${titleName} ${accumatedTitle}`;
    const content = isTrim ? `${accumatedTitle}${titleName}` : `${titleName} ${accumatedTitle}`;

    // 注意有空格
    return {
      name,
      content: (
        <ACard url={jumpUrl ? `/dashboard/game/${gameCode}${jumpUrl}` : ''}>
          <div onClick={jumpToLink} className='dbd-rank-list-title-box'>
            <span className='dbd-rank-list-title-text'>{content}</span>
            <SvgIcon className='dbd-overlay-line-title-icon' type='arrow-right'></SvgIcon>
          </div>
        </ACard>
      )
    };
  }, [locale, filterVal, titleName, accumated]);

  const isSingle = useMemo(() => IsSingleDay({
    start_time: rangeTime?.[0],
    end_time: rangeTime?.[1],
    period: timeType
  }, true), [filterVal]);

  const comChartConfig = useMemo(() => ({
    isHaveBar: true,
    isHaveLine: true,
    isHavePie: true,
    isHaveShare: true,
    isHaveMap: accumated === 'country'
  }), [accumated]);

  const tableColumns: any = useMemo(() => {
    const titleColumnsObj: any = {
      country: formatMessage({ id: 'common_Country' }),
      region: formatMessage({ id: 'common_Region' }),
      channel: formatMessage({ id: 'common_Channel' }),
      zone: formatMessage({ id: 'common_Zone' }),
      os: formatMessage({ id: 'common_System' }),
      lang: formatMessage({ id: 'common_Language' }),
      bundle: formatMessage({ id: 'common_Bundle' }),
    };

    const result = [
      {
        key: 'name',
        header: titleColumnsObj?.[accumated],
        align: 'left',
        dataType: accumated,
      },
      {
        key: componentMetrics?.reqMetricKey,
        header: isSingle ? tableColumnTitle : rangeTableColumnTitle,
        align: 'right',
        dataType: 'integer',
        decimalObj: {
          type: componentMetrics?.type,
          decimal: componentMetrics?.decimal,
          percentDecimals: componentMetrics?.percentDecimals,
        },
        ratioField: 'change',
        valueName: componentMetrics?.metricKey,
      },
      {
        key: 'share_of_all',
        header: formatMessage({ id: 'overview_Share_Of_All' }),
        align: 'right',
        dataType: 'oneDecimalPercent',
      },
    ];
    return result;
  }, [isEn, accumated, componentMetrics, tableColumnTitle, rangeTableColumnTitle, isSingle]);

  const chartTooltipsName = useMemo(() => isEn ? componentMetrics?.enName : componentMetrics?.cnName, [componentMetrics, isEn]);

  return {
    tableColumnTitle,
    prefix,
    titleContent,
    isSingle,
    comChartConfig,
    componentMetrics,
    tableColumns,
    chartTooltipsName,
  };
};